import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import useMaterialBank from "../../hooks/useMaterialBank";

import { FamilyVariant, RawFinishData, SampleItemNumber } from "../../types/AlgoliaTypes";

import SwatchVariantIcon from "../SwatchVariantIcon/SwatchVariantIcon";
import PlaceholderSvg from "../../assets/images/placeholder-images/placeholder_250x250.svg";
import { getObject } from "../../services/algolia";
import NewTag from "./NewTag";
import SteelcaseImage from "../layout/SteelcaseImage/SteelcaseImage";

interface FinishCardProps {
    finish: RawFinishData
    useSteelcaseLink?: boolean;
}

interface ActiveSwatchState {
    objectID: string;
    finishCode: string;
    finishName: string;
    swatchURL: string;
    itemNumber?: SampleItemNumber;
    routerValue: string;
    foeDate?: string;
}

export const FinishCard = ({ finish, useSteelcaseLink }: FinishCardProps) => {

    const { addToCart } = useMaterialBank();

    const [placeholderDataUri] = useState("data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==");
    const [activeSwatch, setActiveSwatch] = useState<ActiveSwatchState>();
    const [colorsHovered, setColorsHovered] = useState<boolean>(false);

    const onIconClick = async (variant: FamilyVariant) => {
        let finishDataObj = null;
        if(variant.id) {
            finishDataObj = await getObject(variant.id)
        }
        
        if(finishDataObj != null) {
            const newSwatch: ActiveSwatchState = {
                objectID: String(variant.id),
                finishCode: String(variant.finishCode),
                finishName: String(variant.finishName) ?? "",
                swatchURL: String(variant.swatchURL),
                routerValue: String(finishDataObj.routerValue),
                itemNumber: finishDataObj.sampleItemNumber,
                foeDate: finishDataObj.foeDate
            }

            if(finish.finishType === "laminate" || finish.finishType === "veneer") {
                newSwatch.swatchURL = finishDataObj?.swatch && finishDataObj.swatch.fileURL ? finishDataObj.swatch.fileURL : placeholderDataUri
            }
            setActiveSwatch(newSwatch);
        }
        
    }

    useEffect(() => {
        
        const initialSwatch: ActiveSwatchState = {
            objectID: finish.objectID,
            finishCode: finish.finishCode,
            finishName: finish.finishName ?? "",
            swatchURL: finish.swatch ? finish.swatch.fileURL : placeholderDataUri,
            routerValue: finish.routerValue,
            itemNumber: finish.sampleItemNumber,
            foeDate: finish.foeDate
        }
        setActiveSwatch(initialSwatch);
        
        
    }, [])

    return (
        <div className={"finish-card"}>
            
            <div className={"finish-card__image-container"}>
                {activeSwatch && activeSwatch.foeDate && Math.floor((new Date().getTime() - new Date(activeSwatch.foeDate).getTime())/(24*3600*1000)) < 91 && <NewTag />}
                {
                    useSteelcaseLink ? 
                    <a href={`https://${window.steelcase_finish_library.siteUrl.host}${window.steelcase_finish_library.siteUrl.path}finish/${activeSwatch?.routerValue}`} target="_blank" rel="noopener noreferrer">
                        <SteelcaseImage className={"finish-card__image"} src={activeSwatch && activeSwatch.swatchURL ? activeSwatch?.swatchURL : placeholderDataUri} deliveryOptions={{seoFileName: activeSwatch?.finishName, format: "webp", width: "1000", height: "1000"}} />
                    </a>
                    :
                    <Link to={`../finish/${activeSwatch?.routerValue}`}>
                        <SteelcaseImage className={"finish-card__image"} src={activeSwatch && activeSwatch.swatchURL ? activeSwatch?.swatchURL : placeholderDataUri} deliveryOptions={{seoFileName: activeSwatch?.finishName, format: "webp", width: "1000", height: "1000"}} />
                    </Link>
                }
                {
                    (activeSwatch?.itemNumber && activeSwatch.itemNumber.sampleItemNumberValue && window.steelcase_finish_library.siteUrl.path == "/") ?
                        <div className="finish-material-card__add-to-cart">
                            <button
                                className="finish-material-card__add-to-cart active"
                                onClick={() => addToCart({ itemNumber: String(activeSwatch?.itemNumber?.sampleItemNumberValue), brands: finish.brandNames.map((item) => item) })}
                            >
                                Add to Cart
                            </button>
                        </div>
                    : null
                }
            </div>
            
            <div className={"finish-card__content"}>
                <span className={"finish-card__brand"}>{ finish.filters && finish.filters.supplier ? finish.filters.supplier[0] : finish.brandNames[0] ? finish.brandNames[0] : "" }</span>
                {
                    useSteelcaseLink ? 
                        <a href={`https://${window.steelcase_finish_library.siteUrl.host}${window.steelcase_finish_library.siteUrl.path}finish/${activeSwatch?.routerValue}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none"}}>
                            <h2 className={"finish-card__name"}>
                            { 
                                finish.parentFabricFamily !== null && 
                                    finish.parentFabricFamily !== undefined && 
                                    finish.parentFabricFamily.familyName !== null && finish.parentFabricFamily.familyName !== undefined ? 
                                        finish.parentFabricFamily.familyName : 
                                        `${finish.finishCode ?? ""} ${finish.finishName ?? ""}` 
                            }
                            </h2>
                        </a>
                    :
                        <Link to={`../finish/${activeSwatch?.routerValue}`} style={{ textDecoration: "none" }}>
                            <h2 className={"finish-card__name"}>
                                { 
                                    finish.parentFabricFamily !== null && 
                                        finish.parentFabricFamily !== undefined && 
                                        finish.parentFabricFamily.familyName !== null && finish.parentFabricFamily.familyName !== undefined ? 
                                            finish.parentFabricFamily.familyName : 
                                            `${finish.finishCode ?? ""} ${finish.finishName ?? ""}` 
                                }
                            </h2>
                        </Link>
                }
                
                <h3 className={"finish-card__type"}>
                    { `${finish.finishType === "fabric" && finish.offering ? `${finish.offering} | ` : ""}`}
                    { `${finish.subClassification && finish.subClassification[0] ? finish.subClassification[0] : ""}` }
                    { `${finish.filters.price?.map((item, idx) => {
                            if(idx === 0 && finish.subClassification){
                                return (
                                    " | " + item
                                )
                            }
                            return (
                                " " + item
                            )
                        }) ?? ""}`
                    }
                </h3>
                {
                    !colorsHovered && finish.variants && finish.variants?.length > 0 &&
                        <span 
                            className={`finish-card__color ${colorsHovered ? "finish-card__color--hide" : ""}`} 
                            onMouseEnter={() => setColorsHovered(true)}
                        >
                            {finish.variants && finish.variants?.length && finish.finishType === "fabric" ? `${finish.variants.length + 1} ${window.steelcase_i18n["steelcase-finish-library"].Colors || "Colors"}` : `${finish.variants.length} variant`}
                        </span>
                }
                {
                    colorsHovered && finish.variants && finish.variants?.length > 0 &&
                        <div className={`finish-variant-icon__container ${colorsHovered ? "finish-variant-icon__container--show" : ""}`} onMouseLeave={() => setColorsHovered(false)}>
                        {
                            <SwatchVariantIcon 
                                id={finish.objectID}
                                finishName={finish.finishName || ""}
                                swatchURL={finish.swatch ? finish.swatch.fileURL : placeholderDataUri} 
                                title={`${finish.parentFabricFamily && finish.parentFabricFamily.familyName ? finish.parentFabricFamily.familyName : finish.finishName ? finish.finishName : ""} ${finish.filters && finish.filters.sub_type && finish.filters.sub_type[0] ? finish.filters.sub_type[0] : ""} in ${finish.finishName && finish.finishName}`}
                                onIconClickHandler={() => 
                                    setActiveSwatch({
                                        objectID: finish.objectID, 
                                        finishName: finish.finishName ?? "", 
                                        finishCode: finish.finishCode, 
                                        swatchURL: finish.swatch ? finish.swatch.fileURL : placeholderDataUri,
                                        routerValue: finish.routerValue,
                                        foeDate: finish.foeDate
                                    })} 
                                isActive={activeSwatch?.finishCode === finish.finishCode} 
                            />
                        }
                        {
                            finish.variants && finish.variants?.length > 5 ? finish.variants?.slice(0, 5).map((item) => {
                                let swatchIconURL = "";
                                let swatchIconAltText = "";
                                if(finish.finishType === "laminate") {
                                    swatchIconURL = finish.swatch ? finish.swatch.fileURL : placeholderDataUri;
                                    swatchIconAltText = item.axis ? item.axis : "";
                                } else {
                                    swatchIconURL = item.swatchURL ? item.swatchURL : placeholderDataUri;
                                    swatchIconAltText = item.finishName ?? "";
                                }
                                return (
                                    // TODO: Sort colors on filter change (Ex: Show all Purple variants first for UX.)
                                    // TODO: (UX) Consider adding a visual representation, letting users how many variants are present in each card matching their filter value.
                                    <SwatchVariantIcon 
                                        key={String(item.id)}
                                        id={String(item.id)} 
                                        finishName={swatchIconAltText} 
                                        title={`${finish.parentFabricFamily && finish.parentFabricFamily.familyName ? finish.parentFabricFamily.familyName : finish.finishName ? finish.finishName : ""} ${finish.filters && finish.filters.sub_type && finish.filters.sub_type[0] ? finish.filters.sub_type[0] : ""} in ${item.finishName && item.finishName}`}
                                        swatchURL={swatchIconURL} 
                                        onIconClickHandler={() => onIconClick(item)} 
                                        isActive={activeSwatch?.finishCode === item.finishCode}
                                    />
                                )
                            })
                            : finish.variants?.map((item) => {
                                let swatchIconURL = "";
                                let swatchIconAltText = "";
                                if(finish.finishType === "laminate") {
                                    swatchIconURL = finish.swatch ? finish.swatch.fileURL : placeholderDataUri;
                                    swatchIconAltText = item.axis ? item.axis : "";
                                } else {
                                    swatchIconURL = item.swatchURL ? item.swatchURL : placeholderDataUri;
                                    swatchIconAltText = item.finishName ?? "";
                                }
                                return (
                                    <SwatchVariantIcon 
                                        key={String(item.id)}
                                        id={String(item.id)} 
                                        finishName={swatchIconAltText} 
                                        swatchURL={swatchIconURL} 
                                        title={`${finish.parentFabricFamily && finish.parentFabricFamily.familyName ? finish.parentFabricFamily.familyName : finish.finishName ? finish.finishName : ""} ${finish.filters && finish.filters.sub_type && finish.filters.sub_type[0] ? finish.filters.sub_type[0] : ""} in ${item.finishName && item.finishName}`}
                                        onIconClickHandler={() => onIconClick(item)} 
                                        isActive={activeSwatch?.finishCode === item.finishCode}
                                    />
                                )
                            })
                        }
                        {
                            useSteelcaseLink && finish.variants && finish.variants.length > 5 &&
                                <a href={`https://${window.steelcase_finish_library.siteUrl.host}${window.steelcase_finish_library.siteUrl.path}finish/${activeSwatch?.routerValue}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                                    <ExtraIcon extraItems={finish.variants?.length - 5} />
                                </a>
                        }
                        {
                            !useSteelcaseLink && finish.variants && finish.variants?.length > 5 && 
                                <Link to={`../finish/${activeSwatch?.routerValue}`} style={{ textDecoration: "none" }}>
                                    <ExtraIcon extraItems={finish.variants?.length - 5} />
                                </Link>
                        }
                        </div>
                }
            </div>
        </div>
    )
}

interface ExtraIconProps {
    extraItems: number;
}

function ExtraIcon({extraItems}: ExtraIconProps) {
    return (
        <>
            <span className={"finish-variant-icon__more-icon"}>
                {extraItems}+
            </span>
        </>
    )
}